<template>
  <div class="course-schedule-display-container">
    <el-row :gutter="20">
      <el-col :span="24">
        <el-card>
          <div slot="header" class="flex justify-between">
            <span>我的日程</span>
          </div>
          <el-form
            :inline="true"
            :model="searchForm"
            class="search-form text-left"
          >
            <el-form-item>
              <el-input
                v-model="searchForm.courseName"
                placeholder="请输入日程名称"
                clearable
                style="width: 200px"
              />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="handleSearch"> 查询 </el-button>
              <el-button @click="handleReset"> 重置 </el-button>
            </el-form-item>
          </el-form>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20">
      <el-col :span="24">
        <el-card class="schedule-card">
          <!-- 日历头部：月份切换（修复图标） -->
          <div class="calendar-header">
            <el-button size="small" @click="prevMonth">
              <i class="el-icon-arrow-left"></i>
            </el-button>
            <span class="calendar-title"
              >{{ currentYear }}年{{ currentMonth }}月</span
            >
            <el-button size="small" @click="nextMonth">
              <i class="el-icon-arrow-right"></i>
            </el-button>
          </div>

          <!-- 日历星期头部 -->
          <div class="calendar-week-header">
            <div class="week-item" v-for="w in weekTextList" :key="w">
              {{ w }}
            </div>
          </div>

          <!-- 日历主体 -->
          <div class="calendar-body" v-loading="loading">
            <div
              class="calendar-day"
              v-for="(item, idx) in calendarList"
              :key="idx"
              :class="{
                'other-month': !item.currentMonth,
                today: item.isToday,
              }"
              @click="handleClickDay(item)"
            >
              <div class="day-num">{{ item.day }}</div>
              <!-- 当天日程 -->
              <div class="day-course" v-if="item.courseList.length">
                <div
                  class="course-tag"
                  v-for="course in item.courseList.slice(0, 2)"
                  :key="course.id"
                  :style="{ backgroundColor: course.courseColor }"
                  @click.stop="openCourseDetail(course)"
                >
                  {{ course.courseName }}
                </div>
                <div class="more-tag" v-if="item.courseList.length > 2">
                  共{{ item.courseList.length }}条
                </div>
              </div>
            </div>
          </div>

          <!-- 日程列表（备用） -->
          <div class="schedule-list" style="margin-top: 20px">
            <el-table :data="tableData" border style="width: 100%" stripe>
              <el-table-column
                prop="courseDate"
                label="日期"
                align="center"
                width="120"
              ></el-table-column>
              <el-table-column
                prop="section"
                label="时段"
                align="center"
                min-width="200"
              >
                <template slot-scope="scope">
                  <template v-if="scope.row.section">
                    <el-tag
                      v-for="sec in scope.row.section.split(',')"
                      :key="sec"
                      size="mini"
                      style="
                        margin: 2px;
                        background: #e6f7ff;
                        color: #1890ff;
                        border: 1px solid #91d5ff;
                      "
                    >
                      {{ getSectionLabel(sec) }}
                    </el-tag>
                  </template>
                </template>
              </el-table-column>

              <el-table-column
                prop="courseName"
                label="日程名称"
                align="center"
                min-width="200"
              ></el-table-column>
              <el-table-column
                prop="room"
                label="会议地点"
                align="center"
                min-width="150"
              ></el-table-column>
              <el-table-column label="日程颜色" align="center" width="120">
                <template slot-scope="scope">
                  <div
                    class="color-block"
                    :style="{
                      backgroundColor: scope.row.courseColor || '#e0e0e0',
                    }"
                  ></div>
                </template>
              </el-table-column>
            </el-table>

            <el-pagination
              :current-page.sync="page.current"
              :page-size="page.size"
              :total="page.total"
              layout="total, sizes, prev, pager, next, jumper"
              style="margin-top: 20px; text-align: right"
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
            />
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 日程详情弹窗 -->
    <el-dialog
      title="日程详情"
      :visible.sync="detailDialogVisible"
      width="600px"
      :close-on-click-modal="false"
      center
    >
      <div v-if="currentCourse" class="course-detail-content">
        <el-descriptions :column="1" border style="margin-bottom: 16px">
          <el-descriptions-item label="会议名称">
            {{ currentCourse.courseName || "无" }}
          </el-descriptions-item>
          <el-descriptions-item label="会议日期">
            {{ currentCourse.courseDate || "无" }}
          </el-descriptions-item>
          <el-descriptions-item label="会议时间">
            {{ getCourseTimeText(currentCourse) }}
          </el-descriptions-item>
          <el-descriptions-item label="会议地点">
            {{ currentCourse.room || "无" }}
          </el-descriptions-item>
          <el-descriptions-item label="参会人员">
            {{ currentCourse.participants || "无" }}
          </el-descriptions-item>
          <el-descriptions-item label="备注">
            {{ currentCourse.remark || "无" }}
          </el-descriptions-item>
          <el-descriptions-item label="附件">
            <div v-if="currentCourse.url" class="attachment-item">
              <el-link
                type="primary"
                @click="downloadAttachment(currentCourse)"
                icon="el-icon-download"
              >
                {{ currentCourse.fileName || "会议附件" }}
              </el-link>
            </div>
            <span v-else style="color: #999">无附件</span>
          </el-descriptions-item>
        </el-descriptions>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getCourseScheduleList } from "@/api/contract/courseScheduleApi";
import { getCommunityId } from "@/api/community/communityApi";

export default {
  name: "CourseScheduleDisplay",
  data() {
    return {
      loading: false,
      // 搜索表单（已清理冗余字段）
      searchForm: {
        courseName: "",
      },
      sectionOptions: [
        { label: "08:00-09:00", value: "480" },
        { label: "09:00-10:00", value: "540" },
        { label: "10:00-11:00", value: "600" },
        { label: "11:00-12:00", value: "660" },
        { label: "12:00-13:00", value: "720" },
        { label: "13:00-14:00", value: "780" },
        { label: "14:00-15:00", value: "840" },
        { label: "15:00-16:00", value: "900" },
        { label: "16:00-17:00", value: "960" },
      ],
      // 星期头部文字
      weekTextList: ["周日", "周一", "周二", "周三", "周四", "周五", "周六"],
      // 日历相关
      currentYear: new Date().getFullYear(),
      currentMonth: new Date().getMonth() + 1,
      calendarList: [],
      // 表格数据
      tableData: [],
      // 分页参数
      page: {
        current: 1,
        size: 10,
        total: 0,
      },
      communityId: "",
      // 详情弹窗相关
      detailDialogVisible: false,
      currentCourse: null,
    };
  },
  created() {
    this.communityId = getCommunityId() || "";
    this.getList();
  },
  methods: {
    // 生成日历数据
    generateCalendar() {
      const year = this.currentYear;
      const month = this.currentMonth;
      const firstDay = new Date(year, month - 1, 1);
      const lastDay = new Date(year, month, 0);
      const firstWeek = firstDay.getDay(); // 当月第一天是周几(0周日)
      const totalDays = lastDay.getDate();

      const now = new Date();
      const todayStr = `${now.getFullYear()}-${String(
        now.getMonth() + 1,
      ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

      let list = [];
      // 补上月末尾天数
      const preMonth = new Date(year, month - 1, 0);
      const preTotalDays = preMonth.getDate();
      for (let i = 0; i < firstWeek; i++) {
        const day = preTotalDays - firstWeek + i + 1;
        list.push({
          day,
          fullDate: `${year}-${String(month - 1).padStart(2, "0")}-${String(
            day,
          ).padStart(2, "0")}`,
          currentMonth: false,
          isToday: false,
          courseList: [],
        });
      }
      // 当月天数
      for (let d = 1; d <= totalDays; d++) {
        const fullDate = `${year}-${String(month).padStart(2, "0")}-${String(
          d,
        ).padStart(2, "0")}`;
        list.push({
          day: d,
          fullDate,
          currentMonth: true,
          isToday: fullDate === todayStr,
          courseList: [],
        });
      }
      // 补下月开头，凑满6行42格
      const remain = 42 - list.length;
      for (let r = 1; r <= remain; r++) {
        list.push({
          day: r,
          fullDate: `${year}-${String(month + 1).padStart(2, "0")}-${String(
            r,
          ).padStart(2, "0")}`,
          currentMonth: false,
          isToday: false,
          courseList: [],
        });
      }

      // 按日期把日程回填到日历格子
      list.forEach((dayItem) => {
        dayItem.courseList = this.tableData.filter(
          (item) => item.courseDate === dayItem.fullDate,
        );
      });

      this.calendarList = list;
    },

    // 上一月
    prevMonth() {
      let m = this.currentMonth - 1;
      let y = this.currentYear;
      if (m < 1) {
        m = 12;
        y -= 1;
      }
      this.currentYear = y;
      this.currentMonth = m;
      this.getList();
    },

    // 下一月
    nextMonth() {
      let m = this.currentMonth + 1;
      let y = this.currentYear;
      if (m > 12) {
        m = 1;
        y += 1;
      }
      this.currentYear = y;
      this.currentMonth = m;
      this.getList();
    },

    // 点击日历日期
    handleClickDay(dayItem) {
      console.log("选中日期：", dayItem.fullDate);
    },

    /**
     * 获取日程列表（切换月份/搜索都会调用）
     */
    async getList() {
      try {
        this.loading = true;
        const startDate = `${this.currentYear}-${String(
          this.currentMonth,
        ).padStart(2, "0")}-01`;
        const endDate = `${this.currentYear}-${String(
          this.currentMonth,
        ).padStart(2, "0")}-31`;

        const params = {
          page: this.page.current,
          row: this.page.size,
          courseName: this.searchForm.courseName,
          communityId: this.communityId,
          startDate,
          endDate,
        };
        const res = await getCourseScheduleList(params);
        if (res && res.code === 0) {
          this.tableData = res.data || [];
          this.page.total = res.total || 0;
          // 生成日历并回填日程
          this.generateCalendar();
        } else {
          this.tableData = [];
          this.page.total = 0;
          this.calendarList = [];
          this.$message.error(res.msg || "获取日程失败");
        }
      } catch (error) {
        console.error("获取日程列表失败:", error);
        this.$message.error("获取日程列表失败，请稍后重试");
        this.tableData = [];
        this.page.total = 0;
        this.calendarList = [];
      } finally {
        this.loading = false;
      }
    },

    /**
     * 搜索
     */
    handleSearch() {
      this.page.current = 1;
      this.getList();
    },

    /**
     * 重置
     */
    handleReset() {
      this.searchForm = {
        courseName: "",
      };
      this.page.current = 1;
      this.getList();
    },

    /**
     * 分页大小改变
     */
    handleSizeChange(val) {
      this.page.size = val;
      this.getList();
    },

    /**
     * 分页页码改变
     */
    handleCurrentChange(val) {
      this.page.current = val;
      this.getList();
    },

    /**
     * 获取时段的完整标签
     */
    getSectionLabel(sectionValue) {
      const sectionItem = this.sectionOptions.find(
        (item) => item.value === sectionValue,
      );
      return sectionItem ? sectionItem.label : sectionValue;
    },

    /**
     * 打开日程详情弹窗
     */
    openCourseDetail(course) {
      this.currentCourse = course;
      this.detailDialogVisible = true;
    },

    /**
     * 获取会议时间文本（修复判空报错）
     */
    getCourseTimeText(course) {
      if (!course || !course.section) return "无";
      const sections = course.section.split(",").map(Number);
      const startSection = Math.min(...sections);
      const endSection = Math.max(...sections);
      const startItem = this.sectionOptions.find(
        (item) => Number(item.value) === startSection,
      );
      const endItem = this.sectionOptions.find(
        (item) => Number(item.value) === endSection,
      );
      if (!startItem || !endItem) return "无";

      // 安全截取开始时间
      let startTime = "";
      if (startItem.label.includes("(")) {
        const arr = startItem.label.split("(");
        if (arr[1]) {
          const temp = arr[1].split("-");
          startTime = temp[0] || "";
        }
      }
      // 安全截取结束时间（移除?.，兼容低版本JS）
      let endTime = "";
      if (endItem.label.includes("(")) {
        const arr = endItem.label.split("(");
        if (arr[1]) {
          const temp = arr[1].split("-");
          if (temp[1]) {
            endTime = temp[1].replace(")", "");
          } else {
            endTime = "";
          }
        }
      }

      if (!startTime || !endTime) return startItem.label;
      return `${startTime} - ${endTime}`;
    },

    /**
     * 从URL解析文件名
     */
    getFileName(url) {
      if (!url) return "";
      const fileName = url.split("/").pop().split("?")[0];
      try {
        return decodeURIComponent(fileName);
      } catch (e) {
        return fileName;
      }
    },

    /**
     * 下载附件
     */
    downloadAttachment(course) {
      if (!course.url) {
        this.$message.warning("暂无附件可下载");
        return;
      }
      try {
        const link = document.createElement("a");
        link.href = course.url;
        link.download = this.getFileName(course.url) || "会议附件";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("下载附件失败：", error);
        this.$message.error("附件下载失败，请稍后重试");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.course-schedule-display-container {
  padding: 20px;
  background-color: #ffffff;

  .search-form {
    .el-form-item {
      margin-bottom: 0;
    }
  }

  .el-card {
    margin-bottom: 20px;
    border-radius: 0;
    box-shadow: none;
  }

  .schedule-card {
    background-color: #fff;
  }

  // 日历头部
  .calendar-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 16px 0;
    font-size: 16px;
    .calendar-title {
      font-weight: bold;
      font-size: 18px;
    }
  }

  // 星期头部
  .calendar-week-header {
    display: flex;
    background: #f5f7fa;
    .week-item {
      flex: 1;
      text-align: center;
      line-height: 40px;
      font-size: 14px;
      color: #333;
      border: 1px solid #ebeef5;
    }
  }

  // 日历主体
  .calendar-body {
    display: flex;
    flex-wrap: wrap;
    .calendar-day {
      width: calc(100% / 7);
      height: 120px;
      border: 1px solid #ebeef5;
      padding: 6px;
      box-sizing: border-box;
      cursor: pointer;
      background: #fff;
      &:hover {
        background: #f8f9fa;
      }
      .day-num {
        font-size: 14px;
        margin-bottom: 4px;
      }
      .day-course {
        overflow: hidden;
      }
      .course-tag {
        font-size: 12px;
        color: #fff;
        padding: 2px 4px;
        border-radius: 3px;
        margin-bottom: 3px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .more-tag {
        font-size: 12px;
        color: #666;
      }
    }
    .other-month {
      background: #fafafa;
      color: #c0c4cc;
    }
    .today {
      background: #e6f7ff;
      .day-num {
        color: #1890ff;
        font-weight: bold;
      }
    }
  }

  // 颜色块
  .color-block {
    width: 30px;
    height: 30px;
    border-radius: 4px;
    display: inline-block;
    border: 1px solid #eee;
    margin: 0 auto;
  }

  // 表格样式
  ::v-deep .el-table th {
    background-color: #f5f5f5;
    border-color: #e8e8e8;
  }
  ::v-deep .el-table--striped .el-table__row--striped td {
    background-color: #fafafa;
  }
  ::v-deep .el-table td,
  ::v-deep .el-table th {
    border-color: #e8e8e8;
  }

  // 详情弹窗
  .course-detail-content {
    padding: 8px 0;
    .attachment-item {
      margin-top: 4px;
    }
  }
  .dialog-footer {
    text-align: center;
  }
}
</style>
